import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import PageBreadcrumb from '../../components/common/PageBreadCrumb';
import { useToggle } from '../../hooks/useToggle';
import PageMeta from '../../components/common/PageMeta';
import { sponsorsList } from '../../services/sponsor';
import { TSponsor } from '../../types/sponsors';
import Input from '../../components/form/input/InputField';
import debounce from 'lodash.debounce';
import { PaginatedResponse } from '../../types/generic';
import { useSimplePagination } from '../../hooks/useSimplePagination';
import CommonTable, {
  type ActionConfig,
  type ColumnConfig,
} from '../../components/common/Table';
import { ListIcon } from '../../icons';
import { useNavigate } from 'react-router';
import { PAGINATION_PER_PAGE } from '../../utils/constants';

const columns: ColumnConfig<TSponsor>[] = [
  {
    key: 'id',
    header: 'ID',
    render: (item) => item.id,
  },
  {
    key: 'name',
    header: 'Name',
  },
  {
    key: 'email',
    header: 'Email',
    className: 'max-w-64 truncate',
    render: (item) => <a href={`mailto:${item.email}`}>{item.email}</a>,
  },
  {
    key: 'createdAt',
    header: 'Created At',
    render: (item) => new Date(item.createdAt).toLocaleString(),
  },
];

const Sponsors = () => {
  const [subscriptions, setSubscriptions] = useState<
    PaginatedResponse<TSponsor>
  >({
    count: 0,
    rows: [],
  });
  const [query, setQuery] = useState('');

  const { totalPages, currentPage, onPageChange, setTotalPages } =
    useSimplePagination();
  const { state: isLoading, on: showLoader, off: hideLoader } = useToggle();
  const navigate = useNavigate();
  
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  };

  const fetchSponsors = async () => {
    if (isLoading) return;
    showLoader();
    const res = await sponsorsList({
      params: { query, page: currentPage, limit: PAGINATION_PER_PAGE },
    }).finally(() => {
      hideLoader();
    });
    setSubscriptions(res);
    setTotalPages(res.count, PAGINATION_PER_PAGE);
  };

  const debouncedResults = useMemo(() => {
    return debounce(fetchSponsors, 500);
    // eslint-disable-next-line
  }, [query, currentPage]);

  useEffect(() => {
    debouncedResults();
    return () => {
      debouncedResults.cancel();
    };
  }, [debouncedResults]);

  const actions: ActionConfig<TSponsor>[] = [
    {
      label: <ListIcon className="hover:cursor-pointer" />,
      onClick: (item) => navigate(`/sponsors/${item.id}`),
      variant: 'primary',
      size: 'sm',
      className: () => 'hover:cursor-pointer',
      title: 'View Learners',
    },
  ];

  return (
    <div>
      <PageMeta title="Sponsors" description="List of Sponsors" />
      <PageBreadcrumb pageTitle="Sponsors" />
      <div className="flex justify-evenly my-4">
        <div className="w-1/2" />
        <div className="w-1/2">
          <Input
            placeholder="Search by Name or Email"
            value={query}
            onChange={handleChange}
          />
        </div>
      </div>
      <CommonTable
        columns={columns}
        data={subscriptions.rows}
        isLoading={isLoading}
        currentPage={currentPage}
        onPageChange={onPageChange}
        showPagination
        totalPages={totalPages}
        actions={actions}
      />
    </div>
  );
};

export default Sponsors;
